import { MgrStageBadge } from "./org/MgrStageBadge";
import { useEffect, useState, useCallback } from "react";
import { isActiveEmp } from "../lib/constants";
import { toast } from "sonner";
import { Wallet, Plus, Check, X, BadgeCheck, HandCoins, Trash2, FileSignature, Loader2, Printer, Paperclip, FileText, Pencil, History } from "lucide-react";
import { AdvanceRepaymentsDialog } from "./AdvanceRepaymentsDialog";
import { Money } from "./SaudiRiyal";
import { Button } from "./ui/button";
import { BulkTools } from "./BulkTools";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { api, fileUrl } from "../lib/api";
import { WaSendButton, EmailDocButton } from "./WaSendButton";
import { htmlPDFBlob } from "../lib/decisionPrint";
import { SAR_SVG } from "../lib/custodyReceipt";
import { letterheadHTML, officialSignatureHTML, withSigner } from "./Letterhead";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { AdvancesExcelBar } from "./AdvancesExcelBar";
import { AdvanceDeductDialog } from "./AdvanceDeductDialog";
import { printAdvanceStatement } from "../lib/advanceStatement";

const STATUS = {
  requested: { cls: "bg-amber-100 text-amber-700" },
  approved: { cls: "bg-blue-100 text-blue-700" },
  signed: { cls: "bg-violet-100 text-violet-700" },
  disbursed: { cls: "bg-emerald-100 text-emerald-700" },
  rejected: { cls: "bg-rose-100 text-rose-700" },
};

const money = (n) => `${Number(n || 0).toLocaleString("en-US")} ${SAR_SVG}`;

const printReceipt = (a, company = {}) => {
  const w = window.open("", "_blank");
  if (!w) return;
  w.document.write(receiptHTML(a, company, true));
  w.document.close();
};

const receiptHTML = (a, company = {}, autoPrint = false) => {
  const sig = a.signature_id ? `<img src="${fileUrl(a.signature_id)}" style="max-height:130px"/>` : "<i>بدون توقيع</i>";
  return (`<!doctype html><html dir="rtl" lang="ar"><head><style>@font-face{font-family:SaudiRiyal;src:url(/fonts/riyal.woff2) format("woff2");unicode-range:U+20C1}</style><meta charset="utf-8"><title>سند قبض</title>
  <style>body{font-family:SaudiRiyal,Tahoma,Arial;padding:32px;color:#0f172a}h1{font-size:22px;margin:0 0 4px}
  .box{max-width:640px;margin:auto;border:1px solid #e2e8f0;border-radius:14px;padding:26px}
  table{width:100%;border-collapse:collapse;margin:16px 0}td{padding:9px 6px;border-bottom:1px solid #eef2f7;font-size:15px}
  .lbl{color:#64748b;width:40%}.amt{font-size:26px;font-weight:800;color:#2563eb}
  .sig{margin-top:20px;border-top:1px dashed #cbd5e1;padding-top:12px}</style></head><body>
  <div class="box">${letterheadHTML(company)}<h1>سند قبض سلفة</h1>
  <div class="amt" style="margin:14px 0">${money(a.amount)}</div>
  <table>
    <tr><td class="lbl">اسم الموظف</td><td>${a.employee_name || "—"}</td></tr>
    <tr><td class="lbl">الفرع</td><td>${a.branch || "—"}</td></tr>
    <tr><td class="lbl">السبب</td><td>${a.reason || "—"}</td></tr>
    <tr><td class="lbl">تاريخ الموافقة</td><td>${(a.approved_at || "").slice(0, 10) || "—"}</td></tr>
    <tr><td class="lbl">تاريخ التوقيع</td><td>${(a.signed_at || "").slice(0, 10) || "—"}</td></tr>
    <tr><td class="lbl">تاريخ الصرف</td><td>${(a.disbursed_at || "").slice(0, 10) || "—"}</td></tr>
  </table>
  <div class="sig" style="display:flex;justify-content:space-between;align-items:flex-end;gap:24px"><div><div style="color:#64748b;font-size:13px">توقيع الموظف باستلام المبلغ:</div>${sig}</div>${officialSignatureHTML(withSigner(company, a))}</div>
  <p style="color:#94a3b8;font-size:12px;margin-top:18px">أقر أنا الموظف باستلام المبلغ المذكور أعلاه سلفة على راتبي.</p>
  </div>${autoPrint ? "<script>window.onload=()=>window.print()</script>" : ""}</body></html>`);
};

const KPI = ({ label, value, tone, onClick, active, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid}
    className={`rounded-2xl border bg-white p-4 text-start transition hover:shadow-md ${active ? "border-blue-400 ring-2 ring-blue-100" : "border-slate-200"}`}>
    <p className="text-xs text-slate-500">{label}</p>
    <p className={`font-heading text-xl font-bold tabular-nums ${tone || "text-slate-800"}`}>{value}</p>
  </button>
);

const CreateDialog = ({ open, onOpenChange, employees, onSaved }) => {
  const { t, dir } = useLang();
  const [empId, setEmpId] = useState("");
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [historical, setHistorical] = useState(false);
  const [date, setDate] = useState("");
  const [repaid, setRepaid] = useState("");
  const [installment, setInstallment] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) { setEmpId(""); setAmount(""); setReason(""); setHistorical(false); setDate(""); setRepaid(""); setInstallment(""); setFiles([]); }
  }, [open]);

  const onPickFiles = async (e) => {
    const picked = Array.from(e.target.files || []);
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const f of picked) {
        const r = await api.uploadFile(f);
        setFiles((prev) => [...prev, { id: r.attachment_id, name: r.attachment_name }]);
      }
    } catch { toast.error(t("dash.adv.fileUploadFail")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    if (!empId || !amount || Number(amount) <= 0) { toast.warning(t("dash.adv.warnAmount")); return; }
    if (historical && !date) { toast.warning(t("dash.adv.warnDate")); return; }
    setBusy(true);
    try {
      await api.createAdvance({
        employee_id: empId, amount: Number(amount), reason,
        historical, date: historical ? date : undefined,
        repaid: historical ? (Number(repaid) || 0) : 0,
        installment: Number(installment) || 0,
        attachments: files,
      });
      toast.success(historical ? t("dash.adv.createdHist") : t("dash.adv.createdNew"));
      onOpenChange(false); onSaved();
    }
    catch (e) { toast.error(e?.response?.data?.detail || t("dash.adv.createFail")); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md font-body max-h-[90vh] overflow-y-auto" dir={dir} data-testid="advance-create-dialog">
        <DialogHeader className="text-right">
          <DialogTitle className="font-heading text-xl">{t("dash.adv.createTitle")}</DialogTitle>
          <DialogDescription className="text-right">{historical ? t("dash.adv.createDescHist") : t("dash.adv.createDescNew")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label data-testid="advance-historical-toggle" className="flex items-center justify-between gap-2 rounded-xl border border-slate-200 bg-slate-50/60 px-3 py-2.5 cursor-pointer">
            <div>
              <div className="text-sm font-semibold text-slate-700">{t("dash.adv.histToggle")}</div>
              <div className="text-[11px] text-slate-400">{t("dash.adv.histHint")}</div>
            </div>
            <input type="checkbox" checked={historical} onChange={(e) => setHistorical(e.target.checked)} className="h-4 w-4 accent-blue-600" />
          </label>
          <div className="space-y-1.5"><Label>{t("dash.adv.employee")}</Label>
            <select data-testid="advance-employee-select" value={empId} onChange={(e) => setEmpId(e.target.value)}
              className="h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm">
              <option value="">{t("dash.adv.pickEmployee")}</option>
              {employees.filter(isActiveEmp).map((e) => <option key={e.id} value={e.id}>{`${e.name} — ${e.branch || "—"}`}</option>)}
            </select>
          </div>
          <div className="space-y-1.5"><Label>{t("dash.adv.amount")}</Label>
            <Input data-testid="advance-amount-input" type="number" dir="ltr" value={amount} onChange={(e) => setAmount(e.target.value)} /></div>
          {historical && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("dash.adv.advDate")}</Label>
                <Input data-testid="advance-date-input" type="date" dir="ltr" value={date} onChange={(e) => setDate(e.target.value)} /></div>
              <div className="space-y-1.5"><Label>{t("dash.adv.repaidBefore")}</Label>
                <Input data-testid="advance-repaid-input" type="number" dir="ltr" placeholder="0" value={repaid} onChange={(e) => setRepaid(e.target.value)} /></div>
            </div>
          )}
          <div className="space-y-1.5"><Label>القسط الشهري (يُخصم من الراتب)</Label>
            <Input data-testid="advance-installment-input" type="number" dir="ltr" placeholder="0 = بدون خصم تلقائي" value={installment} onChange={(e) => setInstallment(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>{t("dash.adv.reason")}</Label>
            <Input data-testid="advance-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>{t("dash.adv.attachments")}</Label>
            <label data-testid="advance-attach-label" className="flex items-center gap-2 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-500 cursor-pointer hover:bg-slate-50">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />} {t("dash.adv.attachBtn")}
              <input type="file" accept="image/*,.pdf" multiple className="hidden" onChange={onPickFiles} data-testid="advance-attach-input" />
            </label>
            {files.length > 0 && (
              <div className="space-y-1">
                {files.map((f, i) => (
                  <div key={f.id} data-testid={`advance-attach-item-${i}`} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-2 py-1 text-xs">
                    <span className="truncate text-slate-600">{f.name}</span>
                    <button type="button" onClick={() => setFiles((p) => p.filter((_, j) => j !== i))} className="text-slate-400 hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
          <Button data-testid="advance-save-btn" disabled={busy || uploading} onClick={save} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {historical ? t("dash.adv.record") : t("dash.adv.create")}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("dash.common.cancel")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const AdvancesPanel = ({ canEdit, employees = [] }) => {
  const { t, dir } = useLang();
  const { company } = useAuth();
  const moneyUI = (n) => <Money value={n} />;
  const moneyTxt = (n) => `${Number(n || 0).toLocaleString("en-US")} ${t("dash.common.riyal")}`;
  const [data, setData] = useState({ items: [], summary: {} });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [repayFor, setRepayFor] = useState(null);
  const [repsFor, setRepsFor] = useState(null);
  const [repayAmt, setRepayAmt] = useState("");
  const [payMethod, setPayMethod] = useState("bank"), [repayMethod, setRepayMethod] = useState("cash");
  const [search, setSearch] = useState("");
  const [fBranch, setFBranch] = useState("");
  const [onlyOut, setOnlyOut] = useState(false);
  const [instFor, setInstFor] = useState(null);
  const [instAmt, setInstAmt] = useState("");

  const load = useCallback(async () => {
    try { setData(await api.listAdvances(filter !== "all" ? { status: filter } : {})); }
    catch { toast.error(t("dash.adv.loadFail")); }
    finally { setLoading(false); }
  }, [filter, t]);
  useEffect(() => { load(); }, [load]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || t("dash.adv.actFail")); } };
  const doRepay = async () => {
    if (!repayAmt || Number(repayAmt) <= 0) { toast.warning(t("dash.adv.warnRepay")); return; }
    await act(() => api.repayAdvance(repayFor.id, { amount: Number(repayAmt), method: repayMethod }), t("dash.adv.repaid"));
    setRepayFor(null); setRepayAmt("");
  };

  const s = data.summary || {};
  const branches = [...new Set((data.items || []).map((a) => a.branch).filter(Boolean))].sort();
  const sq = search.trim().toLowerCase();
  const items = (data.items || []).filter((a) => (!sq || `${a.employee_name || ""} ${a.reason || ""}`.toLowerCase().includes(sq))
    && (!fBranch || a.branch === fBranch) && (!onlyOut || (a.status === "disbursed" && a.outstanding > 0)));
  return (
    <div className="space-y-4" data-testid="advances-panel" dir={dir}>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI testid="adv-kpi-total" onClick={() => { setFilter("all"); setOnlyOut(false); setFBranch(""); setSearch(""); }} active={filter === "all" && !onlyOut} label={t("dash.adv.kpiTotal")} value={s.count || 0} />
        <KPI testid="adv-kpi-pending" onClick={() => { setFilter("requested"); setOnlyOut(false); }} active={filter === "requested"} label={t("dash.adv.kpiPending")} value={s.pending || 0} tone="text-amber-600" />
        <KPI testid="adv-kpi-disbursed" onClick={() => { setFilter("disbursed"); setOnlyOut(false); }} active={filter === "disbursed" && !onlyOut} label={t("dash.adv.kpiDisbursed")} value={moneyUI(s.total_disbursed)} tone="text-blue-600" />
        <KPI testid="adv-kpi-outstanding" onClick={() => { setFilter("disbursed"); setOnlyOut(true); }} active={onlyOut} label={t("dash.adv.kpiOutstanding")} value={moneyUI(s.total_outstanding)} tone="text-rose-600" />
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {["all", "requested", "approved", "signed", "disbursed", "rejected"].map((k) => (
            <button key={k} data-testid={`advance-filter-${k}`} onClick={() => setFilter(k)}
              className={`rounded-full px-3 py-1 text-xs font-semibold border ${filter === k ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-500 border-slate-200"}`}>
              {k === "all" ? t("dash.adv.all") : t(`dash.adv.st.${k}`)}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input data-testid="advance-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder={t("dash.adv.searchPh")} className="h-9 w-44 rounded-md border border-slate-200 bg-white px-3 text-sm" />
          <select data-testid="advance-filter-branch" value={fBranch} onChange={(e) => setFBranch(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
            <option value="">كل الفروع</option>{branches.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <label className="flex items-center gap-1 text-xs text-slate-600" data-testid="advance-filter-outstanding">
            <input type="checkbox" checked={onlyOut} onChange={(e) => { setOnlyOut(e.target.checked); if (e.target.checked) setFilter("disbursed"); }} className="accent-blue-600" /> عليها متبقٍ
          </label>
          <AdvancesExcelBar canEdit={canEdit} onImported={load} />
          {canEdit && <AdvanceDeductDialog onDone={load} />}
          {canEdit && (
            <>
            <BulkTools section="advances" onDone={load} />
            <Button data-testid="advance-create-open" onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              <Plus className="h-4 w-4" /> {t("dash.adv.createOpen")}
            </Button>
            </>
          )}
        </div>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">
          <Wallet className="mx-auto h-10 w-10 text-slate-300" /><p className="mt-2">{t("dash.adv.empty")}</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="bg-slate-50 text-slate-500"><tr>
              <th className="px-3 py-2.5 text-right">{t("dash.adv.colEmployee")}</th><th className="px-3 py-2.5">{t("dash.adv.colAmount")}</th>
              <th className="px-3 py-2.5">{t("dash.adv.colRemaining")}</th><th className="px-3 py-2.5">{t("dash.adv.colStatus")}</th>
              <th className="px-3 py-2.5">{t("dash.adv.colActions")}</th></tr></thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} data-testid={`advance-row-${a.id}`} className="border-t border-slate-100">
                  <td className="px-3 py-2.5 text-right">
                    <div className="font-semibold text-slate-800">{a.employee_name}{a.historical && <span className="mr-1 text-[10px] rounded bg-slate-100 text-slate-500 px-1 py-0.5">{t("dash.adv.histBadge")}</span>}</div>
                    <div className="text-xs text-slate-400">{a.branch || "—"}{a.reason ? ` · ${a.reason}` : ""}{a.historical && a.disbursed_at ? ` · ${String(a.disbursed_at).slice(0, 10)}` : ""}</div>
                    {(a.attachments || []).length > 0 && (
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {a.attachments.map((f, i) => (
                          <a key={f.id} data-testid={`advance-file-${a.id}-${i}`} href={fileUrl(f.id)} target="_blank" rel="noreferrer"
                            className="inline-flex items-center gap-1 rounded bg-blue-50 text-blue-700 px-1.5 py-0.5 text-[11px] hover:bg-blue-100">
                            <Paperclip className="h-3 w-3" /> {f.name || t("dash.adv.attachFallback")}
                          </a>
                        ))}
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center font-mono-nums font-semibold">{moneyUI(a.amount)}</td>
                  <td className="px-3 py-2.5 text-center font-mono-nums">
                    {a.status === "disbursed" ? (() => {
                      const emp = employees.find((e) => e.id === a.employee_id);
                      const sal = Number(emp?.salary) || 0;
                      const pct = sal > 0 ? Math.round((a.outstanding / sal) * 100) : null;
                      return (<div><button type="button" onClick={() => setRepsFor(a)} className="inline-flex items-center gap-1 hover:text-violet-700" title="السدادات وتعديل المتبقي" data-testid={`advance-outstanding-${a.id}`}>{moneyUI(a.outstanding)}{canEdit && <Pencil className="h-3 w-3 text-slate-400" />}</button>{pct != null && <div className="text-[11px] text-slate-400">{t("dash.adv.pctOfSalary", { n: pct })}</div>}</div>);
                    })() : "—"}
                    {a.status !== "rejected" && (
                      <button type="button" disabled={!canEdit} data-testid={`advance-installment-${a.id}`} onClick={() => { setInstFor(a); setInstAmt(a.installment || ""); }}
                        className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-violet-600 hover:underline disabled:no-underline disabled:text-slate-400">
                        قسط: {a.installment ? a.installment : "—"} {canEdit && <Pencil className="h-3 w-3" />}
                      </button>
                    )}
                  </td>
                  <td className="px-3 py-2.5 text-center">
                    <span className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS[a.status]?.cls}`}>{t(`dash.adv.st.${a.status}`)}</span>
                    {a.status === "rejected" && a.reject_reason && <div className="text-[11px] text-rose-400 mt-0.5">{a.reject_reason}</div>}
                    {(a.status === "requested" || a.mgr_stage === "rejected") && <div className="mt-0.5"><MgrStageBadge r={a} testid={`advance-mgr-stage-${a.id}`} /></div>}
                  </td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center justify-center gap-1 flex-wrap">
                      {canEdit && <WaSendButton employeeId={a.employee_id} employeeName={a.employee_name} refType="advance" refId={a.id} testid={`advance-wa-${a.id}`}
                        text={`السلام عليكم ${a.employee_name || ""}\nبخصوص طلب السلفة بمبلغ ${a.amount} ريال: ${a.status === "approved" ? "تمت الموافقة على طلبك ✅" : a.status === "rejected" ? `نعتذر، تم رفض طلبك${a.reject_reason ? ` — السبب: ${a.reject_reason}` : ""}` : "طلبك قيد المراجعة"}.`} />}
                      {canEdit && a.status === "requested" && (
                        <>
                          <button data-testid={`advance-approve-${a.id}`} onClick={() => act(() => api.approveAdvance(a.id), t("dash.adv.approved"))}
                            className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50" title={t("dash.adv.approve")}><Check className="h-4 w-4" /></button>
                          <button data-testid={`advance-reject-${a.id}`} onClick={() => act(() => api.rejectAdvance(a.id, ""), t("dash.adv.rejected"))}
                            className="p-1.5 rounded-lg text-rose-600 hover:bg-rose-50" title={t("dash.adv.reject")}><X className="h-4 w-4" /></button>
                        </>
                      )}
                      {canEdit && (a.status === "signed" || a.status === "disbursed") && (
                        <EmailDocButton employeeId={a.employee_id} employeeName={a.employee_name} refId={a.id} testid={`advance-email-${a.id}`}
                          title={`تعهد واستلام سلفة بمبلغ ${a.amount} ريال`}
                          pdf={async () => ({ blob: await htmlPDFBlob(receiptHTML(a, company || {})), filename: `سلفة-${a.employee_name || ""}.pdf` })} />
                      )}
                      {(a.status === "signed" || a.status === "disbursed") && (
                        <button data-testid={`advance-receipt-${a.id}`} onClick={() => printReceipt(a, company || {})}
                          className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title={t("dash.adv.receipt")}><Printer className="h-4 w-4" /></button>
                      )}
                      {a.journal_number && <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700" title="قيد الصرف" data-testid={`advance-je-${a.id}`}>{a.journal_number}</span>}
                      {a.status === "disbursed" && (a.repayments || []).length > 0 && (
                        <button data-testid={`advance-history-${a.id}`} onClick={() => setRepsFor(a)} className="p-1.5 rounded-lg text-violet-600 hover:bg-violet-50" title="السدادات / تراجع"><History className="h-4 w-4" /></button>
                      )}
                      <button data-testid={`advance-statement-${a.id}`} onClick={() => { if (!printAdvanceStatement((data.items || []).filter((x) => x.employee_id === a.employee_id), company)) toast.error("فعّل النوافذ المنبثقة للطباعة"); }}
                        className="p-1.5 rounded-lg text-blue-600 hover:bg-blue-50" title="كشف سلف الموظف"><FileText className="h-4 w-4" /></button>
                      {canEdit && (a.status === "signed" || (a.status === "approved" && data.sign_required === false)) && (
                        <select value={payMethod} onChange={(e) => setPayMethod(e.target.value)} className="h-7 rounded-lg border border-slate-200 bg-white px-1 text-xs" data-testid={`advance-method-${a.id}`}>
                          <option value="bank">من البنك</option><option value="cash">من الصندوق</option>
                        </select>
                      )}
                      {canEdit && (a.status === "signed" || (a.status === "approved" && data.sign_required === false)) && (
                        <button data-testid={`advance-disburse-${a.id}`} onClick={() => act(() => api.disburseAdvance(a.id, payMethod), t("dash.adv.disbursed"))}
                          className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 text-white px-2 py-1 text-xs hover:bg-emerald-700"><BadgeCheck className="h-3.5 w-3.5" /> {t("dash.adv.disburse")}</button>
                      )}
                      {canEdit && a.status === "disbursed" && a.outstanding > 0 && (
                        <button data-testid={`advance-repay-${a.id}`} onClick={() => { setRepayFor(a); setRepayAmt(""); }}
                          className="inline-flex items-center gap-1 rounded-lg bg-blue-600 text-white px-2 py-1 text-xs hover:bg-blue-700"><HandCoins className="h-3.5 w-3.5" /> {t("dash.adv.repay")}</button>
                      )}
                      {a.status === "approved" && data.sign_required !== false && <span className="inline-flex items-center gap-1 text-xs text-violet-500"><FileSignature className="h-3.5 w-3.5" /> {t("dash.adv.waitSign")}</span>}
                      {canEdit && (
                        <button data-testid={`advance-delete-${a.id}`} onClick={() => act(() => api.deleteAdvance(a.id), t("dash.adv.deleted"))}
                          className="p-1.5 rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600" title={t("dash.adv.del")}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!instFor} onOpenChange={(o) => !o && setInstFor(null)}>
        <DialogContent className="sm:max-w-sm font-body" dir={dir} data-testid="advance-installment-dialog">
          <DialogHeader><DialogTitle>القسط الشهري — {instFor?.employee_name}</DialogTitle>
            <DialogDescription>يُخصم هذا المبلغ شهرياً من الراتب حتى سداد السلفة. ضع 0 لإيقاف الخصم.</DialogDescription></DialogHeader>
          <Input data-testid="advance-installment-amount" type="number" dir="ltr" value={instAmt} onChange={(e) => setInstAmt(e.target.value)} />
          <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <Button data-testid="advance-installment-save" className="bg-violet-600 hover:bg-violet-700"
              onClick={async () => { await act(() => api.setAdvanceInstallment(instFor.id, Number(instAmt) || 0), "تم حفظ القسط"); setInstFor(null); }}>حفظ</Button>
            <Button variant="outline" onClick={() => setInstFor(null)}>{t("dash.common.cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <AdvanceRepaymentsDialog adv={repsFor} onClose={() => setRepsFor(null)} onChanged={load} canEdit={canEdit} />
      <CreateDialog open={createOpen} onOpenChange={setCreateOpen} employees={employees} onSaved={load} />

      <Dialog open={Boolean(repayFor)} onOpenChange={(v) => !v && setRepayFor(null)}>
        <DialogContent className="sm:max-w-sm font-body" dir={dir} data-testid="advance-repay-dialog">
          <DialogHeader className="text-right">
            <DialogTitle className="font-heading text-lg">{t("dash.adv.repayTitle")}</DialogTitle>
            <DialogDescription className="text-right">{t("dash.adv.repayDesc", { amt: moneyTxt(repayFor?.outstanding) })}</DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5"><Label>{t("dash.adv.repayAmount")}</Label>
            <Input data-testid="advance-repay-amount" type="number" dir="ltr" value={repayAmt} onChange={(e) => setRepayAmt(e.target.value)} /></div>
          <div><Label>طريقة الاستلام</Label>
            <select value={repayMethod} onChange={(e) => setRepayMethod(e.target.value)} className="h-10 w-full rounded-lg border border-slate-200 bg-white px-2 text-sm" data-testid="advance-repay-method">
              <option value="cash">نقدي (الصندوق)</option><option value="bank">تحويل بنكي</option>
            </select></div>
          <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <Button data-testid="advance-repay-submit" onClick={doRepay} className="bg-blue-600 hover:bg-blue-700">{t("dash.adv.repaySubmit")}</Button>
            <Button variant="outline" onClick={() => setRepayFor(null)}>{t("dash.common.cancel")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
